import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bot, BookOpen, Trash2, Plus, ExternalLink, Loader2, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin: gestión de libros por clase para Genesis AI.
 * - Lista todas las clases activas.
 * - Permite asociar 1+ links de Google Drive (PDFs) a cada clase.
 * - Estos PDFs alimentan el asistente Genesis AI dentro de Sala Docente.
 */
export default function AdminGenesisLibros() {
  const navigate = useNavigate();
  const [clases, setClases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openClaseId, setOpenClaseId] = useState(null);
  const [driveUrl, setDriveUrl] = useState('');
  const [titulo, setTitulo] = useState('');
  const [uploading, setUploading] = useState(false);
  const [filtro, setFiltro] = useState('activas'); // 'todas' | 'activas' | 'inactivas' | 'con-libros'
  const [busqueda, setBusqueda] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/genesis-ai/clase-libros`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setClases(res.data?.clases || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cargando clases');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const subirLibro = async (claseId) => {
    if (!driveUrl.trim()) { toast.error('Pegá el link de Drive'); return; }
    if (!driveUrl.includes('drive.google.com')) {
      toast.error('Debe ser un link de Google Drive');
      return;
    }
    setUploading(true);
    try {
      const res = await axios.post(
        `${API}/genesis/upload-from-drive`,
        {
          google_drive_url: driveUrl.trim(),
          description: titulo.trim() || 'Libro para Genesis AI',
          clase_id: claseId,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, timeout: 180000 }
      );
      toast.success(`✅ Libro indexado: ${res.data?.filename || 'PDF'} (${res.data?.chunks_processed || 0} fragmentos)`);
      setDriveUrl('');
      setTitulo('');
      setOpenClaseId(null);
      await fetchData();
    } catch (e) {
      toast.error(`❌ ${e?.response?.data?.detail || 'Error subiendo libro'}`);
    } finally {
      setUploading(false);
    }
  };

  const eliminarLibro = async (documentId, filename) => {
    if (!window.confirm(`¿Eliminar libro "${filename}" de Genesis AI?\n\nEsto borrará todos sus fragmentos indexados.`)) return;
    try {
      await axios.delete(`${API}/admin/genesis-ai/clase-libros/${documentId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      toast.success('Libro eliminado');
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error eliminando');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const clasesFiltradas = clases.filter((cl) => {
    // filtro de estado
    if (filtro === 'activas' && !cl.activa) return false;
    if (filtro === 'inactivas' && cl.activa) return false;
    if (filtro === 'con-libros' && (cl.libros_count || 0) === 0) return false;
    // búsqueda
    if (busqueda.trim()) {
      const q = busqueda.trim().toLowerCase();
      const hay = (cl.clase_titulo || '').toLowerCase().includes(q)
        || (cl.categoria || '').toLowerCase().includes(q);
      if (!hay) return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Genesis AI — Libros por Clase</h1>
              <p className="text-sm text-gray-600">
                Vinculá PDFs de Drive a cada clase. Los profesores podrán consultarlos desde Sala Docente.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-sm text-amber-900">
          <strong>Requisitos del PDF:</strong> link directo a un archivo (no carpeta) en Google Drive,
          compartido como <em>"Cualquiera con el link puede ver"</em>.
          Si tu link es de carpeta, abrí el PDF dentro y copiá el link de ese archivo individual.
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4 flex flex-wrap items-center gap-2">
          <input
            type="text"
            placeholder="Buscar clase..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="flex-1 min-w-[180px] px-3 py-1.5 rounded border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            data-testid="input-buscar-clase"
          />
          {[
            { key: 'activas', label: 'Activas' },
            { key: 'inactivas', label: 'Inactivas' },
            { key: 'con-libros', label: 'Con libros' },
            { key: 'todas', label: 'Todas' },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFiltro(f.key)}
              data-testid={`filter-${f.key}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                filtro === f.key
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
          <span className="text-xs text-gray-500 ml-auto" data-testid="clases-count">
            {clasesFiltradas.length} de {clases.length}
          </span>
        </div>

        <div className="space-y-4">
          {clasesFiltradas.map((cl) => (
            <div key={cl.clase_id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-testid={`clase-row-${cl.clase_id}`}>
              <div className="px-4 py-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  {cl.banner_url ? (
                    <img src={cl.banner_url} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded bg-indigo-100 text-indigo-700 flex items-center justify-center shrink-0">
                      <BookOpen className="w-5 h-5" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-900 truncate">{cl.clase_titulo}</p>
                      {cl.activa ? (
                        <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 shrink-0">
                          Activa
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 shrink-0">
                          Inactiva
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      Semestre {(cl.semestres || []).join(', ') || '–'} · {cl.libros_count} libro(s)
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => { setOpenClaseId(openClaseId === cl.clase_id ? null : cl.clase_id); setDriveUrl(''); setTitulo(''); }}
                  size="sm"
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid={`btn-add-libro-${cl.clase_id}`}
                >
                  <Plus className="w-4 h-4 mr-1" /> Agregar libro
                </Button>
              </div>

              {/* Lista de libros existentes */}
              {cl.libros.length > 0 && (
                <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-2 space-y-1">
                  {cl.libros.map((lb) => (
                    <div key={lb.id} className="flex items-center justify-between gap-2 py-1.5 text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <BookOpen className="w-4 h-4 text-emerald-600 shrink-0" />
                        <span className="font-medium text-gray-800 truncate">{lb.filename}</span>
                        <span className="text-xs text-gray-500 shrink-0">{lb.chunk_count} frag.</span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {lb.drive_url && (
                          <a
                            href={lb.drive_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 text-gray-500 hover:text-indigo-600"
                            title="Ver en Drive"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                        <button
                          onClick={() => eliminarLibro(lb.id, lb.filename)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                          title="Eliminar"
                          data-testid={`btn-delete-libro-${lb.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Form para subir nuevo libro (inline) */}
              {openClaseId === cl.clase_id && (
                <div className="border-t border-indigo-100 bg-indigo-50/40 px-4 py-3 space-y-2">
                  <input
                    type="text"
                    placeholder="Título / descripción (opcional)"
                    value={titulo}
                    onChange={(e) => setTitulo(e.target.value)}
                    className="w-full px-3 py-2 rounded border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    data-testid={`input-titulo-${cl.clase_id}`}
                  />
                  <input
                    type="url"
                    placeholder="https://drive.google.com/file/d/.../view"
                    value={driveUrl}
                    onChange={(e) => setDriveUrl(e.target.value)}
                    className="w-full px-3 py-2 rounded border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    data-testid={`input-drive-url-${cl.clase_id}`}
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={() => subirLibro(cl.clase_id)}
                      disabled={uploading}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      size="sm"
                      data-testid={`btn-confirm-add-${cl.clase_id}`}
                    >
                      {uploading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Procesando…</> : 'Subir e indexar'}
                    </Button>
                    <Button onClick={() => setOpenClaseId(null)} variant="outline" size="sm" disabled={uploading}>
                      Cancelar
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    El procesamiento puede tardar 30 seg a 3 min según el tamaño del PDF.
                  </p>
                </div>
              )}
            </div>
          ))}
          {clasesFiltradas.length === 0 && (
            <div className="text-center py-12 text-gray-500" data-testid="empty-state">
              {clases.length === 0
                ? 'No hay clases cargadas en el sistema.'
                : 'Ninguna clase coincide con los filtros. Probá con "Todas".'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
